// frontend/src/components/DocumentUploadAccordion.jsx
// Project:      Vision Systems by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-08-28
// Updated:      2026-09-10 10:50 BST
//
// Purpose:
//   Shared document upload accordion used by job/customer creation flows.
//
// Description:
//   - Lists recent documents for the supplied customer/job context.
//   - Uploads one or more files with document tags and portal visibility flags.
//   - Prefers per-job document routes when a persisted job ID exists.
//   - Falls back to the generic document resolver where required.
//   - Uses the authenticated backend as the sole authority for tenant context.
//
// Change Summary (2026-09-10):
//   - Reviewed the existing 2026-09-08 mobile conversion; no structural rewrite required.
//   - Increased mobile tag-remove controls from 36px to a full 44px touch target.
//   - Added touch-manipulation/touch-pan hints to accordion, upload actions and recent-list controls.
//   - Hardened mobile label/content wrapping without changing data or interaction paths.
//   - Preserved the existing tallest-record recent-upload viewport and vertical scroll snap.
//
// Change Summary (2026-09-08):
//   - Removed tenantId prop, tenant fallback values, tenant query parameters,
//     tenant resolver test parameters, and tenant multipart form fields.
//   - Preserved customer/job context and the existing endpoint fallback strategy.
//   - Added responsive accordion/header/actions for phone layouts.
//   - Converts the recent-upload table into labelled cards below 768px using
//     the same DOM/data path.
//   - Measures all mobile recent-upload cards and sizes the internal viewport to
//     the tallest record so at least one complete document is visible.
//   - Adds vertical scroll snap between complete mobile document records.
//   - Enlarged file, checkbox, tag-remove and action touch targets.
//   - Added stale recent-list response protection.
//   - Recent-list failures now preserve documents already uploaded in-session.
//
// Security Contract:
//   - Frontend never supplies tenant context.
//   - Tenant scope is resolved by the authenticated backend.
//   - customer_id and job_id remain legitimate resource filters/associations.
//
// Operational Notes:
//   - Parent components may still temporarily pass a legacy tenantId prop while
//     they are being migrated. React will ignore that undeclared prop here.
//   - The generic document resolver is still used for endpoint discovery only;
//     no tenant context is supplied to it by this component.
//   - This component does not manipulate ReactGridLayout host height directly;
//     parent widgets/flyouts own their outer layout and scrolling behaviour.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { resolveDocApi } from "../utils/docApiResolver";
import { FaTag } from "react-icons/fa";

const MOBILE_BREAKPOINT = 768;
const MOBILE_RECENT_FALLBACK_HEIGHT = 270;

const inputCls =
  "w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent/40";

const toAbs = (p) => (p?.startsWith("/") ? p : `/${p || ""}`);

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatApiError = (error, fallback) => {
  const status = error?.response?.status;
  const detail =
    (typeof error?.response?.data?.detail === "string" &&
      error.response.data.detail) ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  if (status === 401) return "401 — Authentication required.";
  if (status === 403) return `403 — ${detail || "Access denied."}`;
  return `${status ? `${status} — ` : ""}${detail || fallback}`;
};

function TagInput({ value, onChange, placeholder = "proposal, signed", isMobile }) {
  const [text, setText] = useState("");
  const tags = value || [];

  const add = (raw) => {
    const nextTag = (raw || "").trim();
    if (!nextTag || tags.includes(nextTag)) {
      setText("");
      return;
    }

    onChange([...tags, nextTag]);
    setText("");
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add(text);
    }
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap gap-2 mb-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-300 bg-gray-50 text-[0.82rem] min-w-0"
          >
            <FaTag className="opacity-70 shrink-0" />
            <span className="break-all">{tag}</span>
            <button
              type="button"
              title={`Remove ${tag}`}
              onClick={() => onChange(tags.filter((item) => item !== tag))}
              className={
                "leading-none text-gray-600 hover:text-black select-none rounded-full flex items-center justify-center shrink-0 " +
                (isMobile ? "w-11 h-11 text-lg touch-manipulation" : "w-6 h-6")
              }
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <input
        className={
          inputCls +
          (isMobile ? " min-h-[44px] text-base" : "")
        }
        placeholder={placeholder}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => add(text)}
      />
    </div>
  );
}

export default function DocumentUploadAccordion({
  customerId,
  jobId,
  customerRef,
  jobRef,
  defaultTagsCsv = "",
  defaultPublish = false,
}) {
  const recentRequestSeq = useRef(0);
  const recentRowRefs = useRef(new Map());

  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [tagList, setTagList] = useState([]);
  const [publish, setPublish] = useState(!!defaultPublish);
  const [downloadable, setDownloadable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [okText, setOkText] = useState("");
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [recentNote, setRecentNote] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false
  );
  const [mobileRecentHeight, setMobileRecentHeight] = useState(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    setPublish(!!defaultPublish);
  }, [defaultPublish]);

  useEffect(() => {
    if (!defaultTagsCsv) return;

    if (tagList.length === 0) {
      const seeded = defaultTagsCsv
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      if (seeded.length) {
        setTagList(seeded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTagsCsv]);

  const headerSuffix = useMemo(() => {
    const parts = [];

    if (files.length) {
      parts.push(`${files.length} file${files.length > 1 ? "s" : ""}`);
    }
    if (publish) parts.push("Portal");
    if (downloadable) parts.push("DL");

    return parts.length ? parts.join(" · ") : "";
  }, [files.length, publish, downloadable]);

  const onInput = (event) => {
    const selected = Array.from(event.target.files || []);
    setFiles(selected);
    setErrorText("");
    setOkText("");
    setProgress({ done: 0, total: selected.length });
  };

  const loadRecent = async () => {
    const requestSeq = ++recentRequestSeq.current;

    setLoadingRecent(true);
    setRecentNote("");

    try {
      const items = [];

      if (jobId) {
        try {
          const { data } = await axiosInstance.get(
            toAbs(`jobs/${jobId}/documents`)
          );

          if (requestSeq !== recentRequestSeq.current) return;
          items.push(...listFrom(data));
        } catch {
          // Continue to the generic resolver.
        }
      }

      if (items.length === 0) {
        const params = {};
        if (customerId) params.customer_id = customerId;
        if (jobId) params.job_id = jobId;

        const resolvedApi = await resolveDocApi({
          testParams: params,
        });

        if (requestSeq !== recentRequestSeq.current) return;

        const { data } = await axiosInstance.get(toAbs(resolvedApi.list), {
          params,
        });

        if (requestSeq !== recentRequestSeq.current) return;
        items.push(...listFrom(data));
      }

      if (requestSeq !== recentRequestSeq.current) return;
      setRecent(items.slice(0, 10));
    } catch (error) {
      if (requestSeq !== recentRequestSeq.current) return;

      const detail = formatApiError(
        error,
        "Could not fetch recent documents."
      );

      console.warn(
        "[DocumentUploadAccordion] recent list error:",
        error?.response?.status,
        detail
      );

      setRecentNote(
        `${detail} Documents uploaded during this session remain shown below.`
      );
    } finally {
      if (requestSeq === recentRequestSeq.current) {
        setLoadingRecent(false);
      }
    }
  };

  useEffect(() => {
    if (open) {
      loadRecent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, jobId]);

  const uploadAll = async () => {
    setErrorText("");
    setOkText("");

    if (!files.length) {
      setErrorText("Select at least one file.");
      return;
    }

    if (!customerRef) {
      setErrorText(
        "Customer Ref is required to build the SharePoint path."
      );
      return;
    }

    setBusy(true);

    try {
      const results = [];

      let usePerJob = !!jobId;
      let generic = null;

      if (!usePerJob) {
        const testParams = {};
        if (customerId) testParams.customer_id = customerId;

        generic = await resolveDocApi({
          testParams,
        });
      }

      for (const file of files) {
        const form = new FormData();

        if (customerId) {
          form.append("customer_id", String(customerId));
        }
        if (jobId) {
          form.append("job_id", String(jobId));
        }
        if (customerRef) {
          form.append("customer_ref", customerRef);
        }
        if (jobRef) {
          form.append("job_ref", jobRef);
        }
        if (tagList.length) {
          form.append("tags", tagList.join(","));
        }
        if (publish) {
          form.append("portal_visible", "true");
        }
        if (downloadable) {
          form.append("downloadable", "true");
        }

        form.append("file", file);

        let response;

        try {
          if (usePerJob) {
            response = await axiosInstance.post(
              toAbs(`jobs/${jobId}/documents/upload`),
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } else {
            response = await axiosInstance.post(
              toAbs(generic.upload),
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          }
        } catch (firstError) {
          if (usePerJob) {
            generic =
              generic ||
              (await resolveDocApi({
                testParams: customerId
                  ? { customer_id: customerId }
                  : {},
              }));

            response = await axiosInstance.post(
              toAbs(generic.upload),
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } else if (jobId) {
            response = await axiosInstance.post(
              toAbs(`jobs/${jobId}/documents/upload`),
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } else {
            throw firstError;
          }
        }

        setProgress((previous) => ({
          ...previous,
          done: Math.min(
            (previous.done || 0) + 1,
            previous.total || files.length
          ),
        }));

        if (response?.data) {
          results.push(response.data);
        }
      }

      if (results.length) {
        setRecent((previous) => {
          const unique = new Map();

          [...results, ...previous].forEach((item) => {
            if (!item || (!item.id && !item.name)) return;

            const key =
              item.id ??
              `name:${item.name}:${item.latest_url ?? item.url ?? ""}`;

            if (!unique.has(key)) {
              unique.set(key, item);
            }
          });

          return Array.from(unique.values()).slice(0, 10);
        });
      }

      setOkText(
        `Upload complete (${files.length} file${
          files.length > 1 ? "s" : ""
        }).`
      );

      setFiles([]);
      setProgress({ done: 0, total: 0 });

      await loadRecent();
    } catch (error) {
      setErrorText(
        formatApiError(error, "Upload failed.")
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    recentRowRefs.current.forEach((_, key) => {
      const stillPresent = recent.some((row, index) => {
        const rowKey = row?.id ?? `local-${index}`;
        return rowKey === key;
      });

      if (!stillPresent) {
        recentRowRefs.current.delete(key);
      }
    });
  }, [recent]);

  useEffect(() => {
    if (!isMobile || !open || recent.length === 0) {
      setMobileRecentHeight(null);
      return undefined;
    }

    let frameId = null;
    let observer = null;

    const measureRows = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const heights = Array.from(recentRowRefs.current.values())
          .filter(Boolean)
          .map((node) => Math.ceil(node.getBoundingClientRect().height))
          .filter((height) => height > 0);

        if (heights.length) {
          setMobileRecentHeight(Math.max(...heights));
        }
      });
    };

    measureRows();
    window.addEventListener("resize", measureRows);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureRows);
      recentRowRefs.current.forEach((node) => {
        if (node) observer.observe(node);
      });
    }

    return () => {
      window.removeEventListener("resize", measureRows);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, open, recent]);

  const recentViewportHeight =
    mobileRecentHeight || MOBILE_RECENT_FALLBACK_HEIGHT;

  const mobileCellStyle = isMobile
    ? {
        display: "grid",
        gridTemplateColumns: "92px minmax(0, 1fr)",
        gap: 10,
        alignItems: "start",
        padding: "8px 10px",
        borderBottom: "1px solid #e5e7eb",
        overflowWrap: "anywhere",
      }
    : undefined;

  const renderMobileLabel = (label) =>
    isMobile ? (
      <span className="text-xs font-semibold text-gray-500">
        {label}
      </span>
    ) : null;

  return (
    <div className="border rounded-xl min-w-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={
          "w-full text-left px-4 flex items-center justify-between gap-3 touch-manipulation " +
          (isMobile ? "py-3 min-h-[48px]" : "py-3")
        }
        title="Toggle document upload"
        aria-expanded={open}
      >
        <span className="font-medium min-w-0 break-words">
          📄 Documents
          {headerSuffix ? (
            <span className="text-gray-500 text-sm font-normal">
              {" "}
              — {headerSuffix}
            </span>
          ) : null}
        </span>

        <span
          className="shrink-0 text-gray-500 text-base"
          aria-hidden="true"
        >
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div className="p-3 md:p-4 border-t grid gap-3 min-w-0">
          <div className="grid gap-3 md:grid-cols-2 min-w-0">
            <div className="md:col-span-2 min-w-0">
              <input
                type="file"
                multiple
                onChange={onInput}
                className={
                  "border rounded-lg px-3 py-2 w-full min-w-0 touch-manipulation " +
                  (isMobile ? "min-h-[44px] text-base" : "")
                }
              />

              {files.length > 0 && (
                <div className="text-sm text-gray-700 mt-1 break-words">
                  <b>Files selected:</b> {files.length}
                  {progress.total
                    ? ` | Progress: ${progress.done}/${progress.total}`
                    : ""}
                </div>
              )}
            </div>

            <div className="md:col-span-2 min-w-0">
              <label className="text-xs text-gray-600 mb-1 block">
                Document Tags
              </label>

              <TagInput
                value={tagList}
                onChange={setTagList}
                isMobile={isMobile}
              />

              <div className="text-xs text-gray-500 mt-1">
                These tags will be included with uploads.
              </div>
            </div>

            <label
              className={
                "inline-flex items-center gap-3 text-sm rounded-lg touch-manipulation min-w-0 " +
                (isMobile
                  ? "min-h-[44px] border border-gray-200 px-3 py-2"
                  : "")
              }
            >
              <input
                type="checkbox"
                checked={publish}
                onChange={(event) => setPublish(event.target.checked)}
                className={isMobile ? "w-5 h-5 shrink-0" : ""}
              />
              <span className="min-w-0 break-words">Publish to Portal (visible to customer)</span>
            </label>

            <label
              className={
                "inline-flex items-center gap-3 text-sm rounded-lg touch-manipulation min-w-0 " +
                (isMobile
                  ? "min-h-[44px] border border-gray-200 px-3 py-2"
                  : "")
              }
            >
              <input
                type="checkbox"
                checked={downloadable}
                onChange={(event) =>
                  setDownloadable(event.target.checked)
                }
                className={isMobile ? "w-5 h-5 shrink-0" : ""}
              />
              <span className="min-w-0 break-words">Downloadable by portal users</span>
            </label>
          </div>

          <div className="text-sm text-gray-700 min-w-0">
            <div className="break-words">
              <b>Customer Ref:</b>{" "}
              {customerRef ? (
                <span>{customerRef}</span>
              ) : (
                <span style={{ color: "#b91c1c" }}>
                  required (enter in the parent job/customer form)
                </span>
              )}
            </div>

            {jobRef && (
              <div className="break-words">
                <b>Job Ref:</b> {jobRef}
              </div>
            )}
          </div>

          {errorText && (
            <div className="text-red-700 font-semibold break-words">
              {errorText}
            </div>
          )}

          {okText && (
            <div className="text-green-700 font-semibold break-words">
              {okText}
            </div>
          )}

          <div
            className={
              isMobile
                ? "grid grid-cols-1 gap-2"
                : "flex flex-wrap gap-2"
            }
          >
            <button
              type="button"
              onClick={uploadAll}
              disabled={!files.length || !customerRef || busy}
              className={
                "px-3 rounded-xl text-white font-medium touch-manipulation " +
                (isMobile ? "min-h-[44px] w-full" : "py-1.5")
              }
              style={{
                background: "var(--accent-color, #1e90ff)",
                opacity:
                  busy || !files.length || !customerRef ? 0.6 : 1,
              }}
            >
              {busy
                ? "Uploading…"
                : `Upload ${files.length ? `(${files.length})` : ""}`}
            </button>

            <button
              type="button"
              onClick={loadRecent}
              disabled={loadingRecent}
              className={
                "px-3 rounded-xl bg-gray-100 font-medium touch-manipulation " +
                (isMobile ? "min-h-[44px] w-full" : "py-1.5")
              }
            >
              {loadingRecent ? "Refreshing…" : "Refresh list"}
            </button>
          </div>

          <div className="mt-3 min-w-0">
            <div className="mb-2 break-words text-sm font-semibold">
              Recent uploads for this context
            </div>

            {recentNote && (
              <div className="text-xs text-amber-700 mb-2 break-words">
                {recentNote}
              </div>
            )}

            <div
              className="border rounded-lg min-w-0 touch-pan-y"
              style={
                isMobile && recent.length > 0
                  ? {
                      height: `${recentViewportHeight}px`,
                      maxHeight: `${recentViewportHeight}px`,
                      minHeight: `${recentViewportHeight}px`,
                      overflowY: "auto",
                      overflowX: "hidden",
                      scrollSnapType: "y mandatory",
                      overscrollBehavior: "contain",
                      WebkitOverflowScrolling: "touch",
                    }
                  : {
                      overflow: "auto",
                    }
              }
            >
              <table
                className="min-w-full text-sm"
                style={
                  isMobile
                    ? {
                        display: "block",
                        width: "100%",
                      }
                    : undefined
                }
              >
                <thead
                  style={isMobile ? { display: "none" } : undefined}
                >
                  <tr className="text-left border-b">
                    <th className="py-2 px-3">Name</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Customer</th>
                    <th className="py-2 px-3">Job</th>
                    <th className="py-2 px-3">Portal</th>
                    <th className="py-2 px-3">Download</th>
                    <th className="py-2 px-3">Link</th>
                  </tr>
                </thead>

                <tbody
                  style={
                    isMobile
                      ? {
                          display: "block",
                          width: "100%",
                        }
                      : undefined
                  }
                >
                  {loadingRecent ? (
                    <tr
                      style={
                        isMobile
                          ? {
                              display: "block",
                              width: "100%",
                            }
                          : undefined
                      }
                    >
                      <td
                        colSpan={7}
                        className="py-3 px-3"
                        style={
                          isMobile
                            ? {
                                display: "block",
                                minHeight: 90,
                              }
                            : undefined
                        }
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : recent.length === 0 ? (
                    <tr
                      style={
                        isMobile
                          ? {
                              display: "block",
                              width: "100%",
                            }
                          : undefined
                      }
                    >
                      <td
                        colSpan={7}
                        className="py-3 px-3"
                        style={
                          isMobile
                            ? {
                                display: "block",
                                minHeight: 90,
                              }
                            : undefined
                        }
                      >
                        No documents found for this customer/job yet.
                      </td>
                    </tr>
                  ) : (
                    recent.map((row, index) => {
                      const rowKey = row.id ?? `local-${index}`;
                      const documentUrl = row.latest_url || row.url || "";

                      return (
                        <tr
                          key={rowKey}
                          ref={(node) => {
                            if (node) {
                              recentRowRefs.current.set(rowKey, node);
                            } else {
                              recentRowRefs.current.delete(rowKey);
                            }
                          }}
                          className="border-b last:border-0"
                          style={
                            isMobile
                              ? {
                                  display: "block",
                                  width: "100%",
                                  background: "#fff",
                                  scrollSnapAlign: "start",
                                  scrollSnapStop: "always",
                                }
                              : undefined
                          }
                        >
                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Name")}
                            <span className="break-words min-w-0">
                              {row.name ?? row.filename ?? "—"}
                            </span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Type")}
                            <span className="break-all min-w-0">
                              {row.mime_type || row.mimetype || "—"}
                            </span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Customer")}
                            <span>{row.customer_id ?? "—"}</span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Job")}
                            <span>{row.job_id ?? "—"}</span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Portal")}
                            <span>
                              {(row.portal_visible ?? row.portalVisible)
                                ? "Yes"
                                : "No"}
                            </span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={mobileCellStyle}
                          >
                            {renderMobileLabel("Download")}
                            <span>
                              {row.downloadable ?? false ? "Yes" : "No"}
                            </span>
                          </td>

                          <td
                            className="py-2 px-3"
                            style={
                              isMobile
                                ? {
                                    ...mobileCellStyle,
                                    borderBottom: "none",
                                  }
                                : undefined
                            }
                          >
                            {renderMobileLabel("Link")}
                            <span>
                              {documentUrl ? (
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={
                                    "underline inline-flex items-center touch-manipulation " +
                                    (isMobile ? "min-h-[44px] px-1" : "")
                                  }
                                >
                                  Open
                                </a>
                              ) : (
                                "—"
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-gray-500 mt-2 break-words">
              Tip: Links open the uploaded file in SharePoint. If the folder
              did not exist, the backend creates it.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
